import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import QuestionRenderer from './question_renderer';

class OnboardingQuestionnaire extends React.Component {
  static propTypes = {
    questions: PropTypes.array.isRequired,
    responses: PropTypes.object.isRequired,
    workflowInstanceToken: PropTypes.string.isRequired,
    stepInstanceId: PropTypes.number.isRequired,
    workflowStep: PropTypes.object.isRequired,
    errors: PropTypes.object
  };

  constructor(props) {
    super(props);
    this.state = {
      responses: this.props.responses || {},
      errors: this.props.errors || {},
      isSubmitting: false,
      skipStep: false,
      skipReason: ''
    };
  }

  shouldShowQuestion = (question, responses) => {
    // Always show questions without conditions
    if (!question.conditional_question_id) {
      return true;
    }

    const triggerResponse = responses[question.conditional_question_id];
    if (!triggerResponse) {
      return false;
    }

    const triggerValues = question.conditional_answers || [];
    const operator = question.conditional_operator || 'equals';

    switch (operator) {
      case 'equals':
        return triggerResponse === triggerValues[0];
      case 'includes':
        return triggerValues.includes(triggerResponse);
      case 'not_equals':
        return triggerResponse !== triggerValues[0];
      case 'not_includes':
        return !triggerValues.includes(triggerResponse);
      default:
        return true;
    }
  };

  handleResponseChange = (questionId, value) => {
    this.setState(prevState => {
      const newResponses = {
        ...prevState.responses,
        [questionId]: value
      };

      // Clear responses for questions that are now hidden due to conditional logic
      const updatedResponses = { ...newResponses };
      this.props.questions.forEach(question => {
        if (question.conditional_question_id && !this.shouldShowQuestion(question, newResponses)) {
          delete updatedResponses[question.id];
        }
      });

      return {
        responses: updatedResponses,
        // Clear error for this question when user starts typing
        errors: {
          ...prevState.errors,
          [questionId]: null
        }
      };
    });
  };

  handleSkipToggle = (checked) => {
    this.setState({ 
      skipStep: checked,
      skipReason: checked ? this.state.skipReason : ''
    });
  };

  handleSkipReasonChange = (value) => {
    this.setState({ skipReason: value });
  };

  validateForm = (saveType) => {
    const errors = {};
    
    if (this.state.skipStep) {
      if (!this.state.skipReason.trim()) {
        errors.skip_reason = 'Please explain why this step does not apply.';
      }
      return errors;
    }

    // For "Save Progress", skip validation to allow incomplete forms
    if (saveType === 'progress') {
      return errors;
    }

    // Only validate required fields for "Save and Continue" and only for visible questions
    this.props.questions.forEach(question => {
      if (question.required && this.shouldShowQuestion(question, this.state.responses)) {
        const response = this.state.responses[question.id];
        if (!response || (typeof response === 'string' && !response.trim())) {
          errors[question.id] = 'This field is required.';
        }
      }
    });

    return errors;
  };

  handleSubmit = async (saveType) => {
    const errors = this.validateForm(saveType);
    
    if (Object.keys(errors).length > 0) {
      this.setState({ errors });
      return;
    }

    this.setState({ isSubmitting: true });

    try {
      const formData = new FormData();
      
      // Add responses
      Object.entries(this.state.responses).forEach(([questionId, response]) => {
        formData.append(`responses[${questionId}]`, response);
      });

      // Add skip information
      formData.append('skip_step', this.state.skipStep);
      if (this.state.skipStep) {
        formData.append('skip_reason', this.state.skipReason);
      }

      // Add save type
      formData.append(saveType === 'complete' ? 'save_complete' : 'save_progress', '1');

      // Add CSRF token
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
      if (csrfToken) {
        formData.append('authenticity_token', csrfToken);
      }

      const response = await fetch(`/onboarding/${this.props.workflowInstanceToken}/step/${this.props.stepInstanceId}`, {
        method: 'PATCH',
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Redirect on successful save
          window.location.href = data.redirect_url;
        } else {
          // Handle validation errors
          this.setState({ errors: data.errors || {} });
        }
      } else {
        // Handle HTTP errors
        try {
          const errorData = await response.json();
          this.setState({ errors: errorData.errors || {} });
        } catch (e) {
          console.error('Error parsing response:', e);
          alert('An error occurred while saving. Please try again.');
        }
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('An error occurred while saving. Please try again.');
    } finally {
      this.setState({ isSubmitting: false });
    }
  };

  render() {
    const { questions, workflowStep } = this.props;
    const { responses, errors, isSubmitting, skipStep, skipReason } = this.state;

    return (
      <div className="questionnaire-form">
        {Object.entries(errors).some(([questionId, errorMessage]) => errorMessage && questionId !== 'skip_reason') && (
          <div className="alert alert-danger mb-4">
            <h6><i className="fa fa-exclamation-triangle"></i> Please fix the following errors:</h6>
            <ul className="mb-0">
              {Object.entries(errors)
                .filter(([questionId, errorMessage]) => errorMessage && questionId !== 'skip_reason')
                .map(([questionId, errorMessage]) => {
                  const question = questions.find(q => q.id.toString() === questionId);
                  return (
                    <li key={questionId}>
                      <strong>{question?.question_text || `Question #${questionId}`}:</strong> {errorMessage}
                    </li>
                  );
                })}
            </ul>
          </div>
        )}

        {questions
          .filter(question => this.shouldShowQuestion(question, responses))
          .map(question => (
            <QuestionRenderer
              key={question.id}
              question={question}
              value={responses[question.id] || ''}
              onChange={(value) => this.handleResponseChange(question.id, value)}
              error={errors[question.id]}
            />
          ))}

        {workflowStep.requirement_type === 'optional' && (
          <div className="conditional-skip">
            <div className="checkbox">
              <input
                type="checkbox"
                id="skip_step"
                checked={skipStep}
                onChange={(e) => this.handleSkipToggle(e.target.checked)}
              />
              <label htmlFor="skip_step" className="checkbox m-0">
                Does not apply.
              </label>
            </div>

            {skipStep && (
              <div className="skip-reason">
                <textarea
                  value={skipReason}
                  onChange={(e) => this.handleSkipReasonChange(e.target.value)}
                  placeholder="Please explain..."
                  className={`form-control form-control-sm w-100${errors.skip_reason ? ' is-invalid' : ''}`}
                  rows="4"
                />
                {errors.skip_reason && (
                  <div className="field-error">{errors.skip_reason}</div>
                )}
              </div>
            )}
          </div>
        )}

        <div className="form-actions">
          <button
            type="button"
            onClick={() => this.handleSubmit('progress')}
            disabled={isSubmitting}
            className="btn btn-outline-primary"
          >
            {isSubmitting ? 'Saving...' : 'Save Progress'}
          </button>
          
          <button
            type="button"
            onClick={() => this.handleSubmit('complete')}
            disabled={isSubmitting}
            className="btn btn-primary"
          >
            {isSubmitting ? 'Saving...' : 'Save and Continue'}
          </button>

          {workflowStep.requirement_type !== 'required' && (
            <a href={`/onboarding/${this.props.workflowInstanceToken}`} className="btn btn-outline-default">
              Skip this Step
            </a>
          )}
        </div>
      </div>
    );
  }
}

export default OnboardingQuestionnaire;